import { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  CookiePreferences,
  DEFAULT_PREFERENCES,
  ALL_ACCEPTED_PREFERENCES,
  getStoredPreferences,
  savePreferences,
  hasConsentBeenGiven,
  initializeAnalytics,
  disableAnalytics,
  clearNonNecessaryCookies,
  updateMarketingConsent,
  CookieCategory,
} from '@/lib/cookies';

// Feature flag: Set to false to disable cookie consent banner (not required outside EU)
const COOKIE_CONSENT_ENABLED = false;

interface CookieConsentContextValue {
  // State
  preferences: CookiePreferences;
  hasConsented: boolean;
  showBanner: boolean;
  showPreferences: boolean;

  // Actions
  acceptAll: () => void;
  rejectNonEssential: () => void;
  saveCustomPreferences: (preferences: Partial<CookiePreferences>) => void;
  openPreferences: () => void;
  closePreferences: () => void;
  closeBanner: () => void;

  // Helpers
  isCategoryEnabled: (category: CookieCategory) => boolean;
}

const CookieConsentContext = createContext<CookieConsentContextValue | null>(null);

export function useCookieConsentContext() {
  const context = useContext(CookieConsentContext);
  if (!context) {
    throw new Error('useCookieConsentContext must be used within a CookieConsentProvider');
  }
  return context;
}

export { CookieConsentContext };

export function useCookieConsent() {
  // When cookie consent is disabled, assume all cookies are accepted
  const [preferences, setPreferences] = useState<CookiePreferences>(() => {
    if (!COOKIE_CONSENT_ENABLED) {
      return { ...ALL_ACCEPTED_PREFERENCES, timestamp: Date.now() };
    }
    const stored = getStoredPreferences();
    return stored || DEFAULT_PREFERENCES;
  });

  const [hasConsented, setHasConsented] = useState(() =>
    !COOKIE_CONSENT_ENABLED || hasConsentBeenGiven()
  );
  const [showBanner, setShowBanner] = useState(() =>
    COOKIE_CONSENT_ENABLED && !hasConsentBeenGiven()
  );
  const [showPreferences, setShowPreferences] = useState(false);

  // Initialize analytics on mount if consent was previously given (or consent is disabled)
  useEffect(() => {
    if (!COOKIE_CONSENT_ENABLED) {
      // Auto-initialize analytics when consent is disabled
      initializeAnalytics(ALL_ACCEPTED_PREFERENCES);
      return;
    }
    if (hasConsented) {
      const stored = getStoredPreferences();
      if (stored) {
        initializeAnalytics(stored);
      }
    }
  }, [hasConsented]);

  const acceptAll = useCallback(() => {
    const newPrefs: CookiePreferences = {
      ...ALL_ACCEPTED_PREFERENCES,
      timestamp: Date.now(),
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferences(false);
    initializeAnalytics(newPrefs);
    // Enable marketing consent for GA4 ad tracking
    updateMarketingConsent(true);
  }, []);

  const rejectNonEssential = useCallback(() => {
    const newPrefs: CookiePreferences = {
      ...DEFAULT_PREFERENCES,
      timestamp: Date.now(),
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferences(false);
    disableAnalytics();
    clearNonNecessaryCookies();
  }, []);

  const saveCustomPreferences = useCallback((customPrefs: Partial<CookiePreferences>) => {
    const newPrefs: CookiePreferences = {
      ...preferences,
      ...customPrefs,
      necessary: true, // Always required
      timestamp: Date.now(),
      version: preferences.version,
    };
    setPreferences(newPrefs);
    savePreferences(newPrefs);
    setHasConsented(true);
    setShowBanner(false);
    setShowPreferences(false);

    // Handle analytics based on new preferences
    if (newPrefs.analytics || newPrefs.marketing) {
      initializeAnalytics(newPrefs);
    } else {
      disableAnalytics();
    }

    // Update marketing consent separately for GA4 ad tracking
    updateMarketingConsent(newPrefs.marketing);

    // Clear cookies for disabled categories
    if (!newPrefs.functional || !newPrefs.analytics || !newPrefs.marketing) {
      clearNonNecessaryCookies();
    }
  }, [preferences]);

  const openPreferences = useCallback(() => {
    setShowPreferences(true);
  }, []);

  const closePreferences = useCallback(() => {
    setShowPreferences(false);
  }, []);

  const closeBanner = useCallback(() => {
    // Closing the banner without making a choice is treated as rejecting non-essential
    rejectNonEssential();
  }, [rejectNonEssential]);

  const isCategoryEnabled = useCallback((category: CookieCategory) => {
    return preferences[category];
  }, [preferences]);

  return {
    preferences,
    hasConsented,
    showBanner,
    showPreferences,
    acceptAll,
    rejectNonEssential,
    saveCustomPreferences,
    openPreferences,
    closePreferences,
    closeBanner,
    isCategoryEnabled,
  };
}
